import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function FindMyMatter() {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/client/find-my-matter", { email_or_phone: value });
      setResult(data);
      if (!data.sent) {
        toast.info("No matter found for that contact. Check the email or phone and try again.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex flex-col" data-testid="find-my-matter">
      <SiteHeader />
      <section className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-lg">
          <div className="text-overline text-zinc-500">Lost your portal link?</div>
          <h1 className="mt-3 font-serif text-4xl lg:text-5xl font-medium tracking-tighter leading-none">
            Find your matter.
          </h1>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            Enter the email or phone number you used when starting your transfer. We'll send you a fresh secure link
            to your live transfer workspace.
          </p>

          {!result?.sent && (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="contact">Email or phone</Label>
                <Input
                  id="contact"
                  required
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="you@example.com or +27 82 555 0000"
                  className="rounded-none mt-2"
                  data-testid="find-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800"
                data-testid="find-submit"
              >
                {loading ? "Searching…" : "Send me my link →"}
              </Button>
              <p className="text-xs text-zinc-500 leading-relaxed">
                For security, we don't confirm whether an email or phone is on file. If we find a match, you'll receive a link.
              </p>
            </form>
          )}

          {result?.sent && (
            <div className="mt-8 space-y-4" data-testid="find-result">
              <div className="border border-zinc-200 bg-white p-6">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <h2 className="mt-4 font-serif text-2xl">Link{result.matter_count > 1 ? "s" : ""} sent</h2>
                <p className="mt-2 text-sm text-zinc-600">
                  We found {result.matter_count} active matter{result.matter_count > 1 ? "s" : ""} for that contact. A fresh portal link has been sent.
                </p>
                <p className="mt-2 text-xs text-zinc-500">
                  <Mail className="h-3 w-3 inline mr-1" />
                  Email delivery is currently <span className="font-mono">SIMULATED</span> — you can open your matter directly below.
                </p>
              </div>
              <ul className="space-y-2">
                {result.matters.map((m) => (
                  <li key={m.id} className="border border-zinc-200 bg-white p-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-sm">{m.client_name}</div>
                      <div className="text-xs text-zinc-500 capitalize">{m.service_type} · {m.status?.replace("_", " ")}</div>
                    </div>
                    <button
                      onClick={() => navigate(`/client/${m.portal_token}`)}
                      className="bg-zinc-900 text-white px-4 py-2 text-sm hover:bg-zinc-800 inline-flex items-center gap-1"
                      data-testid={`open-portal-${m.id}`}
                    >
                      Open <ArrowRight className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
              <Link to="/" className="inline-block text-sm text-zinc-500 hover:text-zinc-900">← Back to home</Link>
            </div>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
